import React, { useState } from "react";
import { Send, ShieldCheck, HeadphonesIcon, Plane, MapPinned, Instagram, Mail, Phone, MapPin, ArrowRight } from "lucide-react";
import { TESTIMONIALS, WHY_US, BRAND, BUDGET_TIERS, LOCATIONS } from "../mock";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";

export function CTABanner({ title, desc, cta, image, reverse }) {
  return (
    <section className="bg-white py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-10 items-center ${reverse ? "lg:[&>div:first-child]:order-2" : ""}`}>
          <div>
            <p className="uppercase tracking-[0.3em] text-[#1F5B63] text-xs font-bold mb-3">
              Powered By Trevora
            </p>
            <h2
              className="text-3xl md:text-5xl text-slate-900 font-light leading-tight"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {title}
            </h2>
            <p className="text-slate-600 mt-4 text-lg leading-relaxed">{desc}</p>
            <button className="mt-8 inline-flex items-center gap-2 px-7 py-3 rounded-full bg-[#1F5B63] text-white font-semibold hover:bg-[#164a51] transition-colors">
              {cta} <ArrowRight className="h-4 w-4" />
            </button>
          </div>
          <div className="relative rounded-3xl overflow-hidden aspect-[4/3] shadow-xl">
            <img src={image} alt={title} className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-tr from-[#1F5B63]/40 to-transparent" />
          </div>
        </div>
      </div>
    </section>
  );
}

export function BudgetSection() {
  return (
    <section className="bg-[#FBF6EC] py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="text-center mb-12">
          <p className="uppercase tracking-[0.3em] text-[#1F5B63] text-xs font-bold mb-3">
            Every Wallet, Every Wanderlust
          </p>
          <h2
            className="text-3xl md:text-5xl text-slate-900 font-light"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Your Dream Holiday, Your Budget
          </h2>
          <p className="text-slate-600 mt-3">
            Whether it's luxury or low-cost, we've got the perfect trip waiting for you.
          </p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {BUDGET_TIERS.map((b) => (
            <a
              key={b.label}
              href="#top"
              className="group relative overflow-hidden rounded-2xl aspect-[4/5] shadow-md hover:shadow-2xl transition-all duration-500 hover:-translate-y-1"
            >
              <img src={b.image} alt={b.label} className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-4 text-white text-center">
                <p className="text-[10px] uppercase tracking-widest text-[#F5C05B] font-bold mb-1">{b.tag}</p>
                <h3 className="text-base md:text-lg font-bold">{b.label}</h3>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

export function TestimonialsSection() {
  return (
    <section className="bg-white py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="text-center mb-12">
          <p className="uppercase tracking-[0.3em] text-[#1F5B63] text-xs font-bold mb-3">
            Stories From Our Tribe
          </p>
          <h2
            className="text-3xl md:text-5xl text-slate-900 font-light"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Loved By Travelers Like You
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {TESTIMONIALS.map((t, i) => (
            <div
              key={i}
              className="bg-[#FBF6EC] rounded-2xl p-6 border border-slate-100 hover:shadow-lg transition-shadow"
            >
              <div className="flex flex-wrap gap-2 mb-3">
                {t.tags.map((tag) => (
                  <span
                    key={tag}
                    className="text-[11px] uppercase tracking-wider bg-[#1F5B63]/10 text-[#1F5B63] px-2 py-1 rounded-full font-semibold"
                  >
                    {tag}
                  </span>
                ))}
              </div>
              <p className="text-slate-700 text-sm leading-relaxed">{t.text}</p>
              <div className="flex items-center gap-3 mt-5 pt-5 border-t border-slate-200">
                <img src={t.avatar} alt={t.name} className="h-11 w-11 rounded-full object-cover" />
                <div>
                  <p className="font-semibold text-slate-900">{t.name}</p>
                  <p className="text-xs text-slate-500">Verified Traveler</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const WHY_ICONS = [Plane, ShieldCheck, MapPinned, HeadphonesIcon];

export function WhyUsSection() {
  return (
    <section className="bg-[#1F5B63] py-16 md:py-24 relative overflow-hidden">
      <div className="absolute inset-0 opacity-10" style={{
        backgroundImage: "radial-gradient(circle at 20% 20%, #4FB0BF 0%, transparent 40%), radial-gradient(circle at 80% 80%, #F5C05B 0%, transparent 40%)"
      }}/>
      <div className="max-w-7xl mx-auto px-5 lg:px-8 relative">
        <div className="text-center mb-14">
          <p className="uppercase tracking-[0.3em] text-[#F5C05B] text-xs font-bold mb-3">
            The Trevora Difference
          </p>
          <h2
            className="text-3xl md:text-5xl text-white font-light"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Why Travel Basic, When You Can Trevora?
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {WHY_US.map((w, i) => {
            const Icon = WHY_ICONS[i];
            return (
              <div
                key={w.title}
                className="bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-colors"
              >
                <div className="h-14 w-14 rounded-2xl bg-[#F5C05B] flex items-center justify-center mb-5">
                  <Icon className="h-7 w-7 text-[#1F5B63]" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{w.title}</h3>
                <p className="text-white/75 text-sm leading-relaxed">{w.desc}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export function ContactForm() {
  const [form, setForm] = useState({ name: "", mobile: "", destination: "" });

  const submit = (e) => {
    e.preventDefault();
    if (!form.name || !form.mobile || !form.destination) {
      toast.error("Please fill all fields to plan your trip.");
      return;
    }
    const existing = JSON.parse(localStorage.getItem("trevora_leads") || "[]");
    existing.push({ ...form, at: new Date().toISOString() });
    localStorage.setItem("trevora_leads", JSON.stringify(existing));
    toast.success("Thanks! Our travel expert will call you shortly.");
    setForm({ name: "", mobile: "", destination: "" });
  };

  return (
    <section className="bg-[#FBF6EC] py-16 md:py-24">
      <div className="max-w-5xl mx-auto px-5 lg:px-8">
        <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12 border border-slate-100">
          <div className="text-center mb-8">
            <p className="uppercase tracking-[0.3em] text-[#1F5B63] text-xs font-bold mb-3">
              Plan With Us
            </p>
            <h2
              className="text-3xl md:text-4xl text-slate-900 font-light"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Quick Details = Epic Journey
            </h2>
            <p className="text-slate-600 mt-3">
              Share a few details and we'll craft your perfect getaway.
            </p>
          </div>
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              placeholder="Your Name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="h-12 rounded-full px-5"
            />
            <Input
              placeholder="Mobile No."
              type="tel"
              value={form.mobile}
              onChange={(e) => setForm({ ...form, mobile: e.target.value })}
              className="h-12 rounded-full px-5"
            />
            <Select value={form.destination} onValueChange={(v) => setForm({ ...form, destination: v })}>
              <SelectTrigger className="h-12 rounded-full px-5">
                <SelectValue placeholder="Destination" />
              </SelectTrigger>
              <SelectContent>
                {LOCATIONS.map((l) => (
                  <SelectItem key={l} value={l}>{l}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="md:col-span-3 flex justify-center mt-2">
              <Button
                type="submit"
                className="h-12 px-10 rounded-full bg-[#1F5B63] hover:bg-[#164a51] text-white font-semibold"
              >
                <Send className="h-4 w-4 mr-2" />
                Make It Happen
              </Button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}

export function Footer() {
  return (
    <footer className="bg-[#0F3E44] text-white pt-16 pb-8">
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 pb-10 border-b border-white/10">
          <div>
            <img
              src={BRAND.logo}
              alt="Trevora"
              className="h-14 w-auto object-contain mb-4"
            />
            <p className="text-white/70 text-sm leading-relaxed">
              {BRAND.tagline} We design unforgettable journeys, so you collect stories worth telling for a lifetime.
            </p>
            <div className="flex items-center gap-3 mt-5">
              <a
                href="https://www.instagram.com/trevora.co.in?igsi=a3NqeDBnNWlvOGJi"
                target="_blank"
                rel="noreferrer"
                className="h-10 w-10 rounded-full bg-white/10 hover:bg-[#F5C05B] hover:text-[#0F3E44] flex items-center justify-center transition-colors"
                aria-label="Trevora on Instagram"
              >
                <Instagram className="h-5 w-5" />
              </a>
            </div>
          </div>

          <div>
            <h4 className="font-bold mb-4 uppercase text-sm tracking-wider">Explore</h4>
            <ul className="space-y-2 text-white/70 text-sm">
              {["Domestic Tours", "International Tours", "Honeymoon Packages", "Pilgrimage", "Group Tours", "Upcoming Trips"].map((x) => (
                <li key={x}><a href="#top" className="hover:text-[#F5C05B] transition-colors">{x}</a></li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-bold mb-4 uppercase text-sm tracking-wider">Company</h4>
            <ul className="space-y-2 text-white/70 text-sm">
              {["About Us", "Blog", "Careers", "Terms & Conditions", "Privacy Policy"].map((x) => (
                <li key={x}><a href="#top" className="hover:text-[#F5C05B] transition-colors">{x}</a></li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-bold mb-4 uppercase text-sm tracking-wider">Get in Touch</h4>
            <ul className="space-y-3 text-white/80 text-sm">
              <li className="flex items-start gap-3">
                <Phone className="h-4 w-4 mt-0.5 text-[#F5C05B] shrink-0" />
                <a href={`tel:${BRAND.phoneRaw}`} className="hover:text-[#F5C05B]">{BRAND.phone}</a>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="h-4 w-4 mt-0.5 text-[#F5C05B] shrink-0" />
                <a href={`mailto:${BRAND.email}`} className="hover:text-[#F5C05B]">{BRAND.email}</a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="h-4 w-4 mt-0.5 text-[#F5C05B] shrink-0" />
                <span>{BRAND.address}</span>
              </li>
            </ul>
          </div>
        </div>
        <div className="pt-8 flex flex-col md:flex-row items-center justify-between gap-3 text-white/50 text-xs">
          <p>© {new Date().getFullYear()} Trevora Tours & Travels. All rights reserved.</p>
          <p>Made with love in Gurugram, India.</p>
        </div>
      </div>
    </footer>
  );
}

export function WhatsAppFloating() {
  return (
    <a
      href={`https://wa.me/${BRAND.whatsapp}?text=Hi%20Trevora,%20I'm%20interested%20in%20your%20tour%20packages.`}
      target="_blank"
      rel="noreferrer"
      className="fixed bottom-6 right-6 z-40 h-14 w-14 rounded-full bg-[#25D366] shadow-2xl flex items-center justify-center hover:scale-110 transition-transform"
      aria-label="Chat on WhatsApp"
    >
      <svg viewBox="0 0 24 24" className="h-7 w-7 fill-white" xmlns="http://www.w3.org/2000/svg">
        <path d="M20.52 3.48A11.86 11.86 0 0012.05 0C5.49 0 .16 5.33.16 11.9c0 2.1.55 4.15 1.6 5.96L0 24l6.32-1.66a11.9 11.9 0 005.72 1.46h.01c6.56 0 11.9-5.33 11.9-11.9 0-3.18-1.24-6.17-3.43-8.42zM12.05 21.8h-.01a9.9 9.9 0 01-5.04-1.38l-.36-.21-3.75.98 1-3.66-.24-.38a9.87 9.87 0 01-1.52-5.25c0-5.46 4.45-9.9 9.92-9.9 2.65 0 5.14 1.03 7.01 2.9a9.85 9.85 0 012.9 7 9.91 9.91 0 01-9.91 9.9zm5.44-7.42c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.65.07-.3-.15-1.26-.47-2.4-1.48-.89-.79-1.49-1.77-1.66-2.07-.17-.3-.02-.46.13-.61.13-.13.3-.35.44-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.49s1.07 2.89 1.22 3.09c.15.2 2.1 3.2 5.08 4.49.71.31 1.26.49 1.69.63.71.23 1.35.2 1.86.12.57-.08 1.76-.72 2-1.42.25-.7.25-1.29.17-1.42-.07-.13-.27-.2-.57-.35z" />
      </svg>
    </a>
  );
}
